/* ============================================================
   BASE JS — Vellora Theme
   ============================================================ */

'use strict';

/* ── Cart API ───────────────────────────────────────────────── */
const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(formData) {
    const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  },
  async change(id, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    return res.json();
  },
};

/* ── Cart Drawer ────────────────────────────────────────────── */
const CartDrawer = {
  get drawer() { return document.getElementById('cart-drawer'); },
  get overlay() { return document.getElementById('cart-overlay'); },

  open() {
    this.drawer?.classList.remove('translate-x-full');
    this.overlay?.classList.remove('hidden');
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.render();
  },

  close() {
    this.drawer?.classList.add('translate-x-full');
    this.overlay?.classList.add('hidden');
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  },

  formatMoney(cents, currency) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: currency || 'USD' });
  },

  async render() {
    const cart = await Cart.get();
    const itemsEl = document.getElementById('cart-items');
    const totalEl = document.getElementById('cart-total');

    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });

    if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price, cart.currency);

    if (!itemsEl) return;

    if (cart.items.length === 0) {
      itemsEl.innerHTML = `
        <div class="flex flex-col items-center justify-center h-full text-center py-16">
          <span class="material-symbols-outlined text-5xl text-outline mb-4">shopping_cart</span>
          <p class="text-on-surface-variant text-sm">Your cart is empty.</p>
          <a href="/collections/all" class="mt-4 text-primary underline text-sm font-medium hover:text-primary-container transition-colors">Continue Shopping</a>
        </div>`;
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="flex gap-4 py-5 border-b border-outline-variant/20 last:border-0">
        <a href="${item.url}" class="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-surface-container-low">
          <img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">
        </a>
        <div class="flex-grow min-w-0">
          <a href="${item.url}" class="font-headline text-sm font-semibold text-on-background leading-tight line-clamp-2 hover:text-primary transition-colors">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="text-xs text-on-surface-variant mt-0.5">${item.variant_title}</p>`
            : ''}
          <div class="flex items-center justify-between mt-3">
            <div class="flex items-center border border-outline-variant/30 rounded overflow-hidden">
              <button class="px-2 py-1.5 text-on-surface-variant hover:bg-surface-container transition-colors" onclick="CartDrawer.changeQty('${item.key}', ${item.quantity - 1})">
                <span class="material-symbols-outlined text-[15px]">remove</span>
              </button>
              <span class="px-3 text-xs font-label font-semibold min-w-[2rem] text-center">${item.quantity}</span>
              <button class="px-2 py-1.5 text-on-surface-variant hover:bg-surface-container transition-colors" onclick="CartDrawer.changeQty('${item.key}', ${item.quantity + 1})">
                <span class="material-symbols-outlined text-[15px]">add</span>
              </button>
            </div>
            <span class="font-body text-sm font-semibold text-primary">${this.formatMoney(item.line_price, cart.currency)}</span>
          </div>
        </div>
        <button class="flex-shrink-0 text-outline hover:text-error transition-colors self-start pt-0.5" onclick="CartDrawer.changeQty('${item.key}', 0)" aria-label="Remove">
          <span class="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>`).join('');
  },

  async changeQty(key, quantity) {
    await Cart.change(key, Math.max(0, quantity));
    this.render();
  },
};

/* ── Add to Cart ────────────────────────────────────────────── */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[action="/cart/add"]');
  if (!form) return;
  e.preventDefault();

  const btn = form.querySelector('[type="submit"]');
  if (!btn) return;

  const originalHTML = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-symbols-outlined animate-spin" style="font-size:18px">progress_activity</span>';

  try {
    const formData = new FormData(form);
    await Cart.add(formData);
    CartDrawer.open();
  } catch (err) {
    alert(err.message);
  } finally {
    btn.innerHTML = originalHTML;
    btn.disabled = false;
  }
});

/* ── Cart Drawer Events ─────────────────────────────────────── */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-open-cart]')) { CartDrawer.open(); return; }
  if (e.target.closest('#close-cart') || e.target.closest('#cart-overlay')) CartDrawer.close();
});

/* ── Mobile Menu ────────────────────────────────────────────── */
document.addEventListener('click', (e) => {
  const menu = document.getElementById('mobile-menu');
  const overlay = document.getElementById('mobile-overlay');
  const isOpen = !menu?.classList.contains('-translate-x-full');

  if (e.target.closest('[data-menu-toggle]')) {
    menu?.classList.toggle('-translate-x-full');
    menu?.classList.toggle('translate-x-0');
    overlay?.classList.toggle('hidden');
    menu?.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
    return;
  }

  if (e.target.closest('#close-mobile-menu') || e.target.closest('#mobile-overlay')) {
    menu?.classList.add('-translate-x-full');
    menu?.classList.remove('translate-x-0');
    overlay?.classList.add('hidden');
    menu?.setAttribute('aria-hidden', 'true');
  }
});

/* ── Product Gallery ─────────────────────────────────────────── */
document.addEventListener('click', (e) => {
  const thumb = e.target.closest('[data-thumbnail]');
  if (!thumb) return;

  const src = thumb.dataset.src;
  const srcset = thumb.dataset.srcset;
  const mainImg = document.getElementById('product-main-image');
  if (!mainImg || !src) return;

  mainImg.src = src;
  if (srcset) mainImg.srcset = srcset;

  document.querySelectorAll('[data-thumbnail]').forEach(t => {
    t.classList.remove('border-primary', 'border-2', 'opacity-100');
    t.classList.add('opacity-70', 'border-transparent', 'border');
  });
  thumb.classList.remove('opacity-70', 'border-transparent', 'border');
  thumb.classList.add('border-primary', 'border-2', 'opacity-100');
});

/* ── Quantity Selector ──────────────────────────────────────── */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-qty-btn]');
  if (!btn) return;

  const input = btn.closest('[data-qty-wrapper]')?.querySelector('[data-qty-input]');
  if (!input) return;

  const current = parseInt(input.value) || 1;
  const min = parseInt(input.min) || 1;

  if (btn.dataset.qtyBtn === 'increase') input.value = current + 1;
  if (btn.dataset.qtyBtn === 'decrease') input.value = Math.max(min, current - 1);
});

/* ── Variant Selector ───────────────────────────────────────── */
document.addEventListener('change', (e) => {
  const variantSelect = e.target.closest('[data-variant-select]');
  if (!variantSelect) return;

  const form = variantSelect.closest('form');
  if (!form) return;

  const hiddenId = form.querySelector('[name="id"]');
  if (hiddenId) hiddenId.value = variantSelect.value;

  const price = variantSelect.options[variantSelect.selectedIndex]?.dataset.price;
  const comparePrice = variantSelect.options[variantSelect.selectedIndex]?.dataset.comparePrice;
  const available = variantSelect.options[variantSelect.selectedIndex]?.dataset.available;

  if (price) {
    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl) priceEl.textContent = (parseInt(price) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  const addBtn = form.querySelector('[type="submit"]');
  if (addBtn && available === 'false') {
    addBtn.disabled = true;
    addBtn.textContent = 'Sold Out';
  } else if (addBtn) {
    addBtn.disabled = false;
    addBtn.textContent = 'Add to Cart';
  }
});

/* ── Sticky Nav Scroll ──────────────────────────────────────── */
const header = document.querySelector('header[data-header]');
if (header) {
  window.addEventListener('scroll', () => {
    header.classList.toggle('header-scrolled', window.scrollY > 20);
  }, { passive: true });
}

/* ── Get a Quote Modal ──────────────────────────────────────── */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-open-quote]')) {
    const modal = document.getElementById('quote-modal');
    if (modal) {
      modal.classList.remove('opacity-0', 'pointer-events-none');
      modal.classList.add('opacity-100');
      document.body.classList.add('overflow-hidden');
    }
  }
  if (e.target.closest('[data-close-quote]') || e.target.closest('[data-quote-overlay]')) {
    const modal = document.getElementById('quote-modal');
    if (modal) {
      modal.classList.add('opacity-0', 'pointer-events-none');
      modal.classList.remove('opacity-100');
      document.body.classList.remove('overflow-hidden');
    }
  }
});

/* ── Search Expand ──────────────────────────────────────────── */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-search-toggle]')) {
    const bar = document.getElementById('search-bar');
    const input = bar?.querySelector('input');
    bar?.classList.toggle('hidden');
    if (input && !bar?.classList.contains('hidden')) input.focus();
  }
});

/* ── Init ───────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.render().catch(() => {});
});
